import type { LineProperty } from './types'

/** One property as the editor holds it: every part a string, plus an id for React. */
export interface PropertyRow {
  id: string
  key: string
  value: string
  unit: string
  source: string | null
}

let nextId = 0
const newId = () => `property-${++nextId}`

export function newPropertyRow(): PropertyRow {
  return { id: newId(), key: '', value: '', unit: '', source: null }
}

export function propertyRows(properties: LineProperty[] | null | undefined): PropertyRow[] {
  return (properties ?? []).map((p) => ({
    id: newId(),
    key: p.key,
    value: p.value,
    unit: p.unit ?? '',
    source: p.source ?? null,
  }))
}

/** The API's LineProperties.MaxValueLength. */
export const MAX_PROPERTY_VALUE_LENGTH = 2000

const DIMENSIONS = ['largeur', 'hauteur', 'profondeur']

/**
 * A line's detail is composed from its properties once it has some, so a description typed before
 * becomes its first rows, as free text (no name), rather than being lost: one row per line, since a
 * property's value is one line, and a line too long for a property cut to fit.
 */
export function propertyRowsFromDescription(description: string | null | undefined): PropertyRow[] {
  return (description ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => {
      const parts: string[] = []
      for (let i = 0; i < line.length; i += MAX_PROPERTY_VALUE_LENGTH) parts.push(line.slice(i, i + MAX_PROPERTY_VALUE_LENGTH))
      return parts
    })
    .map((value) => ({ ...newPropertyRow(), value }))
}

/** Why the API would refuse these properties, said before sending them; null when they can be saved. */
export function propertiesProblem(rows: PropertyRow[]): string | null {
  const dimensions = toLineProperties(rows)
    .map((p) => p.key.toLowerCase())
    .filter((key) => DIMENSIONS.includes(key))
  if (new Set(dimensions).size < dimensions.length)
    return 'Une seule largeur, hauteur et profondeur par ligne : la deuxième ne serait pas imprimée.'
  return null
}

/** What the API takes: trimmed, the rows with no value dropped, the source kept. */
export function toLineProperties(rows: PropertyRow[]): LineProperty[] {
  return rows
    .map((r) => ({ key: r.key.trim(), value: r.value.trim(), unit: r.unit.trim() || null, source: r.source }))
    .filter((p) => p.value !== '')
}
